/**
 * CartBootstrapStep — auto-bootstrap shell (Bill 2026-04-28).
 *
 * Click "New Acct" → this route mounts → fires bootstrap() immediately with
 * the signed-in officer's officerCode + active branch → useCartBootstrap
 * navigates to /rao/{cartId}.
 *
 * The previous "Start a new application" form (Opening Officer text input +
 * Branch + Cancel/Continue buttons) was friction with no payoff — both
 * fields are already locked from sign-in identity:
 *   - officerCode comes from /v3/session/my-context (AuthContext)
 *   - branch comes from primary or today's override (also AuthContext)
 *
 * If a manager/supervisor needs to "open on behalf of another officer" that
 * lives in a separate later feature (per cart-visibility scope memory) — it
 * is NOT a free-text override on cart creation.
 *
 * Branch override for today still happens via the global TopBar branch chip
 * (BranchOverrideModal); since that override updates AuthContext before the
 * officer hits "New Acct", the bootstrap picks up the right branch
 * automatically.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { useCartBootstrap } from '../hooks/useCartBootstrap';

export function CartBootstrapStep(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state: configState } = useConfig();
  const { submitting, error, bootstrap } = useCartBootstrap();

  const officerCode = user?.officerCode ?? user?.upn?.split('@')[0] ?? '';
  const activeBranch = user?.activeBranchCode ?? user?.branchCode ?? '';

  // Single-shot bootstrap on mount. Strict-mode + re-render guards ensure
  // we don't fire a second cart-create if React mounts the component twice
  // (dev-mode StrictMode behavior).
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    if (configState === 'loading') return;
    if (!officerCode || !activeBranch) return;
    firedRef.current = true;
    void bootstrap({
      officerCode,
      branchId: activeBranch,
      intent: 'open-new',
    });
    // bootstrap is stable; intentionally not in deps to avoid re-fire
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configState, officerCode, activeBranch]);

  // Failure state — show error + retry/cancel.
  if (error) {
    return (
      <div className="rao-bootstrap" role="alert">
        <header className="rao-bootstrap__header">
          <h1 className="rao-bootstrap__title">Could not start a new cart</h1>
          <p className="rao-bootstrap__subtitle">{error}</p>
        </header>
        <div className="rao-bootstrap__actions rao-bootstrap__field--wide">
          <Button variant="ghost" type="button" onClick={() => navigate('/rao')}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={() => {
              firedRef.current = false;
              void bootstrap({
                officerCode,
                branchId: activeBranch,
                intent: 'open-new',
              });
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  // Loading state — concise; the bootstrap typically resolves in well under
  // a second on a warm Function host. Keeping this simple so the officer
  // doesn't perceive an extra screen.
  return (
    <div className="rao-bootstrap" aria-busy={submitting ? 'true' : 'false'}>
      <header className="rao-bootstrap__header">
        <h1 className="rao-bootstrap__title">Starting a new cart…</h1>
        <p className="rao-bootstrap__subtitle">
          Officer {officerCode || '—'} · Branch {activeBranch || '—'}
        </p>
      </header>
    </div>
  );
}

export default CartBootstrapStep;
